import { createAsyncThunk } from '@reduxjs/toolkit';
import { ApiError, get } from 'aws-amplify/api';
import {
	RHYME_API_SERVICES,
	CaseModel,
	DraftModel,
	DataGatheringRequiredResponse,
	CanWithdrawResponse,
} from 'type-declarations';
import { FetchTransactionThunkResponse } from './models';
import { getActiveSubmissions } from './transaction-utilities';
import { fixBackendValidationErrorIssues } from './fix-backend-errors-utilities';
import { baseRetry } from './../utils';

const rhymeApiUrl = RHYME_API_SERVICES.RhymeApi;

async function caseService_GetCase(
	organizationId: string,
	transactionId: string
): Promise<CaseModel | null> {
	const path = transactionId.startsWith('C-')
		? `organization/${organizationId}/case/${transactionId}`
		: `organization/${organizationId}/case?partnerCaseId=${transactionId}`;

	try {
		const { body } = await baseRetry(
			() =>
				get({
					apiName: rhymeApiUrl,
					path: path,
				}).response
		);
		return (await body.json()) as unknown as CaseModel;
	} catch (error) {
		if (error instanceof ApiError && error.response && error.response.statusCode === 404) {
			return null;
		} else throw error;
	}
}

async function caseService_CanWithdraw(
	organizationId: string,
	caseId: string
): Promise<CanWithdrawResponse | null> {
	const path = `organization/${organizationId}/case/${caseId}/withdraw`;
	const { body } = await get({
		apiName: rhymeApiUrl,
		path: path,
	}).response;
	return (await body.json()) as unknown as CanWithdrawResponse;
}

async function draftService_GetDraft(
	organizationId: string,
	transactionId: string
): Promise<DraftModel | null> {
	const path = transactionId.startsWith('C-')
		? `organization/${organizationId}/draft?caseId=${transactionId}`
		: `organization/${organizationId}/draft/${transactionId}`;

	try {
		const { body } = await baseRetry(
			() =>
				get({
					apiName: rhymeApiUrl,
					path: path,
				}).response
		);
		const draftResponse = (await body.json()) as unknown as DraftModel;
		draftResponse.errors = fixBackendValidationErrorIssues(draftResponse.errors);
		return draftResponse;
	} catch (error) {
		if (error instanceof ApiError && error.response && error.response.statusCode === 404) {
			return null;
		} else throw error;
	}
}

async function scalableDataGatheringApi_GetDataGatheringRequiredForSubmission(
	organizationId: string,
	caseId: string,
	submissionId: string
): Promise<DataGatheringRequiredResponse | null> {
	const path = `organization/${organizationId}/case/${caseId}/submission/${submissionId}/data-gathering/required`;

	try {
		const { body } = await get({
			apiName: rhymeApiUrl,
			path: path,
		}).response;
		const sdgRequirement = (await body.json()) as unknown as DataGatheringRequiredResponse;
		//BACKEND JUNK - identifying details should be included in the response. Adding submissionId here.
		sdgRequirement.submissionId = submissionId;
		return sdgRequirement;
	} catch (error) {
		if (error instanceof ApiError && error.response && error.response.statusCode === 404) {
			return null;
		} else throw error;
	}
}

async function getAllSdgData(theCase: CaseModel): Promise<DataGatheringRequiredResponse[]> {
	//Fetch all SDG requirements associated to every submission on the case.
	const sdgResponses = await Promise.all(
		theCase.submissions.map((s) =>
			scalableDataGatheringApi_GetDataGatheringRequiredForSubmission(
				theCase.organizationId,
				theCase.caseId,
				s.submissionId
			)
		)
	);
	return sdgResponses.filter((resp) => !!resp);
}

export const fetchTransaction = createAsyncThunk(
	'transaction-thunk/fetch-transaction',
	async ({ organizationId, transactionId }: { organizationId: string; transactionId: string }) => {
		const draftCaseResponse = await Promise.all([
			draftService_GetDraft(organizationId, transactionId),
			caseService_GetCase(organizationId, transactionId),
		]);

		const theCase = draftCaseResponse[1];
		let dataGatheringDetails = [] as DataGatheringRequiredResponse[];
		let canWithdrawResponse = null as CanWithdrawResponse | null;
		if (theCase) {
			theCase.submissions = getActiveSubmissions(theCase);
			const apiResponses = await Promise.all([
				getAllSdgData(theCase),
				caseService_CanWithdraw(organizationId, theCase.caseId),
			]);
			dataGatheringDetails = apiResponses[0];
			canWithdrawResponse = apiResponses[1];
		}

		const response: FetchTransactionThunkResponse = {
			draft: draftCaseResponse[0],
			case: theCase,
			dataGatheringDetails: dataGatheringDetails,
			canWithdrawResponse: canWithdrawResponse,
		};
		return response;
	}
);
